import React, { useState, useRef, useEffect } from 'react';
import './Admin.css';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "../authConfig";

const API_URL = process.env.REACT_APP_API_URL;

function formatText(textarea, tag) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const before = textarea.value.substring(0, start);
  const selected = textarea.value.substring(start, end);
  const after = textarea.value.substring(end);
  let open, close;
  if (tag === 'b') { open = '<b>'; close = '</b>'; }
  if (tag === 'i') { open = '<i>'; close = '</i>'; }
  return before + open + selected + close + after;
}

const PANEL_KEYS = ['news', 'whitepapers', 'analytics', 'opportunities'];
const PANEL_META = {
  news: {
    title: 'Manage News Articles',
    subheader: 'Create, edit, and manage news articles.',
    description: 'Add, edit, and update news articles for the /news page.'
  },
  whitepapers: {
    title: 'White Papers',
    subheader: 'Manage white papers.',
    description: 'Upload and organize white papers for a limited audience.'
  },
  analytics: {
    title: 'Analytics',
    subheader: 'View site analytics.',
    description: 'See traffic and engagement analytics.'
  },
  opportunities: {
    title: 'Manage Jobs',
    subheader: 'Manage job announcements.',
    description: 'Add and update job announcements on the /opportunities page.'
  }
};

export default function Admin() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ title: '', date: '', body: '', image: null });
  const [formError, setFormError] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [articles, setArticles] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', date: '', body: '', image: null });
  const [editError, setEditError] = useState('');
  const [expandedPanel, setExpandedPanel] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const bodyRef = useRef();
  const editBodyRef = useRef();
  const [whitepapers, setWhitepapers] = useState([]);
  const [wpLoading, setWpLoading] = useState(false);
  const [wpUpload, setWpUpload] = useState({ name: '', pdf: null });
  const [wpUploadError, setWpUploadError] = useState('');
  const [wpSuccess, setWpSuccess] = useState('');
  const [wpShowLinkModal, setWpShowLinkModal] = useState(false);
  const [wpLinkModalId, setWpLinkModalId] = useState(null);
  const [wpLinks, setWpLinks] = useState([]);
  const [wpLinksLoading, setWpLinksLoading] = useState(false);
  const [wpLinksError, setWpLinksError] = useState('');
  const [wpLinkForm, setWpLinkForm] = useState({ expires_at: '', note: '' });
  const [wpLinkFormLoading, setWpLinkFormLoading] = useState(false);
  const [wpLinkFormError, setWpLinkFormError] = useState('');
  const [wpLinkCopySuccess, setWpLinkCopySuccess] = useState('');
  const [wpLinkEdit, setWpLinkEdit] = useState({ uuid: null, expires_at: '', note: '', loading: false, error: '' });
  const [wpEditNameId, setWpEditNameId] = useState(null);
  const [wpEditNameValue, setWpEditNameValue] = useState('');
  const [wpEditNameLoading, setWpEditNameLoading] = useState(false);
  const [wpEditNameError, setWpEditNameError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobForm, setJobForm] = useState({ path: '', agency: '', title: '', location: '' });
  const [editJobIdx, setEditJobIdx] = useState(null);
  const [editJobForm, setEditJobForm] = useState({ path: '', agency: '', title: '', location: '' });
  const [jobError, setJobError] = useState('');

  useEffect(() => {
    if (isAuthenticated) fetchArticles();
  }, [isAuthenticated]);

  useEffect(() => {
    document.title = 'Admin';
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const fetchArticles = async () => {
    const res = await fetch(`${API_URL}/api/news`);
    const data = await res.json();
    setArticles(data);
  };

  const fetchWhitepapers = async () => {
    setWpLoading(true);
    const res = await fetch(`${API_URL}/api/whitepapers`);
    const data = await res.json();
    setWhitepapers(data);
    setWpLoading(false);
  };

  useEffect(() => { if (isAuthenticated) fetchWhitepapers(); }, [isAuthenticated]);

  // Replace getAuthHeaders with async MSAL version
  const getAuthHeaders = async () => {
    if (!accounts || accounts.length === 0) return {};
    try {
      const request = { ...loginRequest, account: accounts[0] };
      const response = await instance.acquireTokenSilent(request);
      return { 'Authorization': `Bearer ${response.accessToken}` };
    } catch (err) {
      // Optionally handle token acquisition errors (e.g., force login)
      await instance.loginPopup(loginRequest);
      return {};
    }
  };

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    setForm(f => ({ ...f, [name]: files ? files[0] : value }));
  };

  const handleFormat = (tag) => {
    const textarea = bodyRef.current;
    setForm(f => ({ ...f, body: formatText(textarea, tag) }));
    setTimeout(() => textarea.focus(), 0);
  };

  const handleAddArticle = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.title || !form.date || !form.body) {
      setFormError('All fields except image are required.');
      return;
    }
    const data = new FormData();
    data.append('title', form.title);
    data.append('date', form.date);
    data.append('body', form.body);
    if (form.image) data.append('image', form.image);
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/news`, {
        method: 'POST',
        headers,
        body: data
      });
      if (res.status === 401 || res.status === 403) {
        instance.logoutPopup();
        setFormError('Session expired. Please log in again.');
        return;
      }
      if (res.ok) {
        const result = await res.json();
        setSuccess('Article created!');
        setArticleUrl(result.url);
        setForm({ title: '', date: '', body: '', image: null });
        setShowAdd(false);
        fetchArticles();
      } else {
        const result = await res.json();
        setFormError(result.message || 'Failed to create article');
      }
    } catch (err) {
      setFormError('Network error');
    }
    setLoading(false);
  };

  // Edit functionality
  const startEdit = (article) => {
    setEditId(article.id);
    setEditForm({ title: article.title, date: article.date, body: article.body, image: null });
    setEditError('');
  };
  const handleEditChange = (e) => {
    const { name, value, files } = e.target;
    setEditForm(f => ({ ...f, [name]: files ? files[0] : value }));
  };
  const handleEditFormat = (tag) => {
    const textarea = editBodyRef.current;
    setEditForm(f => ({ ...f, body: formatText(textarea, tag) }));
    setTimeout(() => textarea.focus(), 0);
  };
  const handleUpdateArticle = async (e) => {
    e.preventDefault();
    setEditError('');
    if (!editForm.title || !editForm.date || !editForm.body) {
      setEditError('All fields except image are required.');
      return;
    }
    const data = new FormData();
    data.append('title', editForm.title);
    data.append('date', editForm.date);
    data.append('body', editForm.body);
    if (editForm.image) data.append('image', editForm.image);
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/news/${editId}`, {
        method: 'PUT',
        headers,
        body: data
      });
      if (res.ok) {
        setEditId(null);
        fetchArticles();
      } else {
        const result = await res.json();
        setEditError(result.message || 'Failed to update article');
      }
    } catch (err) {
      setEditError('Network error');
    }
    setLoading(false);
  };

  // Upload whitepaper
  const handleWpUploadChange = (e) => {
    const { name, value, files } = e.target;
    setWpUpload(f => ({ ...f, [name]: files ? files[0] : value }));
  };
  const handleWpUpload = async (e) => {
    e.preventDefault();
    setWpUploadError('');
    setWpSuccess('');
    if (!wpUpload.name || !wpUpload.pdf) {
      setWpUploadError('Name and PDF required.');
      return;
    }
    const data = new FormData();
    data.append('name', wpUpload.name);
    data.append('pdf', wpUpload.pdf);
    setWpLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/whitepapers`, {
        method: 'POST',
        headers,
        body: data
      });
      if (res.ok) {
        setWpSuccess('Upload successful!');
        setWpUpload({ name: '', pdf: null });
        fetchWhitepapers();
      } else {
        const result = await res.json();
        setWpUploadError(result.message || 'Upload failed');
      }
    } catch (err) {
      setWpUploadError('Network error');
    }
    setWpLoading(false);
  };
  // Delete whitepaper
  const handleWpDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this white paper and all its links?')) return;
    setWpLoading(true);
    const headers = await getAuthHeaders();
    await fetch(`${API_URL}/api/whitepapers/${id}`, { method: 'DELETE', headers });
    fetchWhitepapers();
    setWpLoading(false);
  };

  // Fetch links when modal opens or whitepaper changes
  useEffect(() => {
    if (wpShowLinkModal && wpLinkModalId) {
      fetchWpLinks();
    }
    // eslint-disable-next-line
  }, [wpShowLinkModal, wpLinkModalId]);

  const fetchWpLinks = async () => {
    setWpLinksLoading(true);
    setWpLinksError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/whitepapers/${wpLinkModalId}/links`, {
        method: 'GET',
        headers
      });
      if (!res.ok) throw new Error('Failed to fetch links');
      const data = await res.json();
      setWpLinks(data);
    } catch (err) {
      setWpLinksError('Failed to load links');
    }
    setWpLinksLoading(false);
  };

  const handleWpLinkFormChange = (e) => {
    const { name, value } = e.target;
    setWpLinkForm(f => ({ ...f, [name]: value }));
  };

  const handleWpLinkCreate = async (e) => {
    e.preventDefault();
    setWpLinkFormLoading(true);
    setWpLinkFormError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/whitepapers/${wpLinkModalId}/links`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          expires_at: wpLinkForm.expires_at || undefined,
          note: wpLinkForm.note || undefined
        })
      });
      if (!res.ok) throw new Error('Failed to create link');
      setWpLinkForm({ expires_at: '', note: '' });
      fetchWpLinks();
    } catch (err) {
      setWpLinkFormError('Failed to create link');
    }
    setWpLinkFormLoading(false);
  };

  const handleWpLinkDelete = async (uuid) => {
    if (!window.confirm('Delete this link?')) return;
    setWpLinksLoading(true);
    const headers = await getAuthHeaders();
    await fetch(`${API_URL}/api/whitepapers/links/${uuid}`, { method: 'DELETE', headers });
    fetchWpLinks();
    setWpLinksLoading(false);
  };

  const handleWpLinkCopy = async (uuid) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/whitepaper/${uuid}`);
      setWpLinkCopySuccess('Copied!');
      setTimeout(() => setWpLinkCopySuccess(''), 1200);
    } catch {
      setWpLinkCopySuccess('Copy failed');
      setTimeout(() => setWpLinkCopySuccess(''), 1200);
    }
  };

  const handleWpLinkEditStart = (link) => {
    setWpLinkEdit({ uuid: link.uuid, expires_at: link.expires_at || '', note: link.note || '', loading: false, error: '' });
  };

  const handleWpLinkEditCancel = () => {
    setWpLinkEdit({ uuid: null, expires_at: '', note: '', loading: false, error: '' });
  };

  const handleWpLinkEditSave = async () => {
    setWpLinkEdit(edit => ({ ...edit, loading: true, error: '' }));
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/whitepapers/links/${wpLinkEdit.uuid}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          expires_at: wpLinkEdit.expires_at || null,
          note: wpLinkEdit.note || null
        })
      });
      if (!res.ok) throw new Error('Failed to update link');
      setWpLinkEdit({ uuid: null, expires_at: '', note: '', loading: false, error: '' });
      fetchWpLinks();
    } catch (err) {
      setWpLinkEdit(edit => ({ ...edit, loading: false, error: 'Failed to update link' }));
    }
  };

  // Panel rendering logic
  const renderPanel = (key, minimal = false) => {
    if (minimal || !expandedPanel) {
      return (
        <div className="admin-panel-minimal-content">
          <h2 className="admin-panel-title">{PANEL_META[key].title}</h2>
          <div className="admin-panel-subheader">{PANEL_META[key].subheader}</div>
          <div className="admin-panel-description">{PANEL_META[key].description}</div>
        </div>
      );
    }
    // Expanded view
    if (key === expandedPanel) {
      if (key === 'news') {
        return (
          <div className="admin-panel-expanded-content">
            <h2 className="admin-panel-expanded-title">Manage News Articles</h2>
            {success && <div className="admin-panel-success">{success} <a href={articleUrl} target="_blank" rel="noopener noreferrer">View Article</a></div>}
            {!showAdd && (
              <button className="admin-btn admin-btn-primary" onClick={() => setShowAdd(true)}>
                Add News Article
              </button>
            )}
            {showAdd && (
              <form onSubmit={handleAddArticle} className="admin-panel-form">
                <div>
                  <div className="admin-input-group">
                    <label>Title</label>
                    <input name="title" type="text" value={form.title} onChange={handleFormChange} className="admin-input" />
                  </div>
                  <div className="admin-input-group">
                    <label>Date</label>
                    <input name="date" type="date" value={form.date} onChange={handleFormChange} className="admin-input" />
                  </div>
                  <div className="admin-input-group">
                    <label>Article Body</label>
                    <div className="admin-format-buttons">
                      <button type="button" onClick={() => handleFormat('b')} className="admin-format-button"><b>B</b></button>
                      <button type="button" onClick={() => handleFormat('i')} className="admin-format-button"><i>I</i></button>
                    </div>
                    <textarea name="body" ref={bodyRef} value={form.body} onChange={handleFormChange} rows={7} className="admin-textarea" />
                  </div>
                  <div className="admin-input-group">
                    <label>Image Upload</label>
                    <input name="image" type="file" accept="image/*" onChange={handleFormChange} className="admin-input" />
                  </div>
                  {formError && <div className="admin-error">{formError}</div>}
                  <div className="admin-actions">
                    <button type="submit" className="admin-btn admin-btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Article'}
                    </button>
                    <button type="button" onClick={() => setShowAdd(false)} className="admin-btn admin-btn-secondary">
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            )}
            {/* List of articles with edit option */}
            <div className="admin-panel-list">
              <h3 className="admin-panel-list-title">Existing Articles</h3>
              <div className="admin-panel-list-content">
                {articles.length === 0 && <div>No articles found.</div>}
                {articles.map((article, idx) => (
                  <div key={article.id} className="admin-panel-list-item">
                    {article.image && (
                      <img
                        src={`${API_URL}${article.image}`}
                        alt=""
                        className="admin-panel-list-item-image"
                      />
                    )}
                    <div className="admin-panel-list-item-details">
                      <div className="admin-panel-list-item-title">{article.title}</div>
                      <div className="admin-panel-list-item-date">{article.date}</div>
                      <div className="admin-panel-list-item-excerpt" dangerouslySetInnerHTML={{ __html: article.excerpt }} />
                      <div className="admin-panel-list-item-actions">
                        <a href={article.url} target="_blank" rel="noopener noreferrer" className="admin-btn-link">View</a>
                        <button onClick={() => startEdit(article)} className="admin-btn-neutral">Edit</button>
                        <button onClick={() => handleDeleteArticle(article.id)} className="admin-btn-danger">Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Edit form */}
              {editId && (
                <form onSubmit={handleUpdateArticle} className="admin-panel-edit-form">
                  <h4 className="admin-panel-edit-title">Edit Article</h4>
                  <div className="admin-input-group">
                      <label>Title</label>
                    <input name="title" type="text" value={editForm.title} onChange={handleEditChange} className="admin-input" />
                    </div>
                  <div className="admin-input-group">
                      <label>Date</label>
                    <input name="date" type="date" value={editForm.date} onChange={handleEditChange} className="admin-input" />
                    </div>
                  <div className="admin-input-group">
                      <label>Article Body</label>
                    <div className="admin-format-buttons">
                      <button type="button" onClick={() => handleEditFormat('b')} className="admin-format-button"><b>B</b></button>
                      <button type="button" onClick={() => handleEditFormat('i')} className="admin-format-button"><i>I</i></button>
                      </div>
                    <textarea name="body" ref={editBodyRef} value={editForm.body} onChange={handleEditChange} rows={7} className="admin-textarea" />
                    </div>
                  <div className="admin-input-group">
                      <label>Image Upload</label>
                    <input name="image" type="file" accept="image/*" onChange={handleEditChange} className="admin-input" />
                    </div>
                  {editError && <div className="admin-error">{editError}</div>}
                  <div className="admin-actions">
                    <button type="submit" className="admin-btn admin-btn-primary" disabled={loading}>
                        {loading ? 'Saving...' : 'Update Article'}
                      </button>
                    <button type="button" onClick={() => setEditId(null)} className="admin-btn admin-btn-secondary">
                        Cancel
                      </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        );
      }
      if (key === 'whitepapers') {
        return (
          <div className="admin-panel-expanded-content">
            <h2 className="admin-panel-expanded-title">White Papers</h2>
            {/* Upload form */}
            <form onSubmit={handleWpUpload} className="admin-panel-form">
              <div className="admin-input-group">
                <label htmlFor="wp-upload-name">Document Name</label>
                <input id="wp-upload-name" name="name" type="text" placeholder="Document Name" value={wpUpload.name} onChange={handleWpUploadChange} className="admin-input" />
              </div>
              <div className="admin-input-group">
                <label htmlFor="wp-upload-file">PDF Upload</label>
                <input id="wp-upload-file" name="pdf" type="file" accept="application/pdf" onChange={handleWpUploadChange} className="admin-input" />
              </div>
              <span className="admin-input-span">{wpUpload.pdf && wpUpload.pdf.name}</span>
              <button type="submit" className="admin-btn admin-btn-primary" disabled={wpLoading}>Upload</button>
              {wpUploadError && <div className="admin-error">{wpUploadError}</div>}
              {wpSuccess && <div className="admin-panel-success">{wpSuccess}</div>}
            </form>
            {/* List of whitepapers */}
            <div className="admin-panel-list">
              {wpLoading && <div>Loading...</div>}
              {!wpLoading && whitepapers.length === 0 && <div>No white papers uploaded.</div>}
              {!wpLoading && whitepapers.map(wp => (
                <div key={wp.id} className="admin-panel-list-item admin-whitepaper-row">
                  <div className="admin-whitepaper-info">
                    <span className="admin-panel-list-item-icon" aria-hidden="true">📄</span>
                    {wpEditNameId === wp.id ? (
                      <input
                        type="text"
                        value={wpEditNameValue}
                        onChange={e => setWpEditNameValue(e.target.value)}
                        className="admin-input"
                        autoFocus
                        aria-label="Edit document name"
                      />
                    ) : (
                      <span className="admin-panel-list-item-title">{wp.name}</span>
                    )}
                  </div>
                  <div className="admin-whitepaper-actions">
                    {wpEditNameId === wp.id ? (
                      <>
                        <button
                          onClick={async () => {
                            if (!wpEditNameValue.trim()) {
                              setWpEditNameError('Name cannot be empty');
                              return;
                            }
                            setWpEditNameLoading(true);
                            setWpEditNameError('');
                            try {
                              const headers = await getAuthHeaders();
                              const res = await fetch(`${API_URL}/api/whitepapers/${wp.id}`, {
                                method: 'PUT',
                                headers: {
                                  ...headers,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ name: wpEditNameValue.trim() })
                              });
                              if (!res.ok) throw new Error('Failed to update name');
                              setWpEditNameId(null);
                              setWpEditNameValue('');
                              fetchWhitepapers();
                            } catch (err) {
                              setWpEditNameError('Failed to update name');
                            }
                            setWpEditNameLoading(false);
                          }}
                          className="admin-btn admin-btn-primary"
                          disabled={wpEditNameLoading}
                        >Save</button>
                        <button
                          onClick={() => { setWpEditNameId(null); setWpEditNameValue(''); }}
                          className="admin-btn admin-btn-neutral"
                          disabled={wpEditNameLoading}
                        >Cancel</button>
                        {wpEditNameError && <span className="admin-error">{wpEditNameError}</span>}
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { setWpEditNameId(wp.id); setWpEditNameValue(wp.name); }}
                          className="admin-btn admin-btn-neutral"
                          aria-label={`Edit name for ${wp.name}`}
                        >Edit</button>
                        <button onClick={() => handleWpDelete(wp.id)} className="admin-btn admin-btn-danger" aria-label={`Delete ${wp.name}`}>Delete</button>
                        <button onClick={() => { setWpLinkModalId(wp.id); setWpShowLinkModal(true); }} className="admin-btn admin-btn-primary" aria-label={`Generate link for ${wp.name}`}>Generate Link</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Link modal (scaffold) */}
            {wpShowLinkModal && (
              <div className="admin-modal-overlay">
                <div className="admin-modal">
                  <button onClick={() => setWpShowLinkModal(false)} className="admin-modal-close" aria-label="Close">×</button>
                  <h3 className="admin-modal-title">Manage Links</h3>
                  {/* Create link form */}
                  <form onSubmit={handleWpLinkCreate} className="admin-panel-form">
                    <div className="admin-input-group">
                      <label htmlFor="wp-link-expiration">Expiration Date</label>
                      <input id="wp-link-expiration" name="expires_at" type="date" value={wpLinkForm.expires_at} onChange={handleWpLinkFormChange} className="admin-input" placeholder="Expiration" />
                    </div>
                    <div className="admin-input-group">
                      <label htmlFor="wp-link-note">Note (optional)</label>
                      <input id="wp-link-note" name="note" type="text" value={wpLinkForm.note} onChange={handleWpLinkFormChange} className="admin-input" placeholder="Note (optional)" />
                    </div>
                    <button type="submit" className="admin-btn admin-btn-primary" disabled={wpLinkFormLoading}>Create</button>
                    {wpLinkFormError && <div className="admin-error">{wpLinkFormError}</div>}
                  </form>
                  {/* List of links */}
                  {wpLinksLoading ? <div>Loading links...</div> : wpLinksError ? <div className="admin-error">{wpLinksError}</div> : (
                    <div className="admin-panel-list">
                      {wpLinks.length === 0 && <div>No links yet.</div>}
                      {wpLinks.map(link => (
                        <div key={link.uuid} className="admin-panel-list-item">
                          <div className="admin-link-row">
                            <a href={`/whitepaper/${link.uuid}`} target="_blank" rel="noopener noreferrer" className="admin-btn-link">
                              {window.location.origin}/whitepaper/{link.uuid}
                            </a>
                            <div className="admin-link-actions">
                              <button onClick={() => handleWpLinkCopy(link.uuid)} className="admin-btn-neutral">Copy</button>
                              <button onClick={() => handleWpLinkDelete(link.uuid)} className="admin-btn-danger">Delete</button>
                              <button onClick={() => handleWpLinkEditStart(link)} className="admin-btn admin-btn-neutral">Edit</button>
                            </div>
                          </div>
                          {(link.expires_at || link.note) && (
                            <div className="admin-link-details">
                              {link.expires_at && <div className="admin-link-detail"><b>Expires:</b> {link.expires_at}</div>}
                              {link.note && <div className="admin-link-detail"><b>Note:</b> {link.note}</div>}
                            </div>
                          )}
                        </div>
                      ))}
                      {wpLinkEdit.uuid && (
                        <div className="admin-link-edit-modal-overlay">
                          <div className="admin-link-edit-modal">
                            <div className="admin-input-group">
                              <label htmlFor="edit-exp-modal">Expiration Date</label>
                              <input
                                id="edit-exp-modal"
                                name="expires_at"
                                type="date"
                                value={wpLinkEdit.expires_at || ''}
                                onChange={e => setWpLinkEdit(edit => ({ ...edit, expires_at: e.target.value }))}
                                className="admin-input"
                              />
                            </div>
                            <div className="admin-input-group">
                              <label htmlFor="edit-note-modal">Note (optional)</label>
                              <input
                                id="edit-note-modal"
                                name="note"
                                type="text"
                                value={wpLinkEdit.note || ''}
                                onChange={e => setWpLinkEdit(edit => ({ ...edit, note: e.target.value }))}
                                className="admin-input"
                              />
                            </div>
                            {wpLinkEdit.error && <div className="admin-error">{wpLinkEdit.error}</div>}
                            <div className="admin-link-edit-actions">
                              <button onClick={handleWpLinkEditSave} className="admin-btn admin-btn-primary" disabled={wpLinkEdit.loading}>Save</button>
                              <button onClick={handleWpLinkEditCancel} className="admin-btn admin-btn-neutral">Cancel</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {wpLinkCopySuccess && <div className="admin-panel-success">{wpLinkCopySuccess}</div>}
                </div>
              </div>
            )}
          </div>
        );
      }
      if (key === 'analytics') {
        return (
          <div className="admin-panel-expanded-content">
            <h2 className="admin-panel-expanded-title">Analytics</h2>
            <div className="admin-analytics-placeholder">Coming soon...</div>
          </div>
        );
      }
      if (key === 'opportunities') {
        return (
          <div className="admin-panel-expanded-content">
            <h2 className="admin-panel-expanded-title">Manage Jobs</h2>
            <form onSubmit={handleAddJob} className="admin-panel-form admin-jobs-form">
              <div className="admin-input-group">
                <label htmlFor="job-path" className="admin-label sr-only">Path</label>
                <input id="job-path" name="path" type="text" placeholder="Path (e.g. Inalab / ... )" value={jobForm.path} onChange={e => setJobForm(f => ({ ...f, path: e.target.value }))} className="admin-input" />
              </div>
              <div className="admin-input-group">
                <label htmlFor="job-agency" className="admin-label sr-only">Agency/Department</label>
                <input id="job-agency" name="agency" type="text" placeholder="Agency/Department" value={jobForm.agency} onChange={e => setJobForm(f => ({ ...f, agency: e.target.value }))} className="admin-input" />
              </div>
              <div className="admin-input-group">
                <label htmlFor="job-title" className="admin-label sr-only">Job Title</label>
                <input id="job-title" name="title" type="text" placeholder="Job Title" value={jobForm.title} onChange={e => setJobForm(f => ({ ...f, title: e.target.value }))} className="admin-input" />
              </div>
              <div className="admin-input-group">
                <label htmlFor="job-location" className="admin-label sr-only">Location</label>
                <input id="job-location" name="location" type="text" placeholder="Location" value={jobForm.location} onChange={e => setJobForm(f => ({ ...f, location: e.target.value }))} className="admin-input" />
              </div>
              <button type="submit" className="admin-btn admin-btn-primary admin-jobs-add-btn">Add Job</button>
              {jobError && <div className="admin-error">{jobError}</div>}
            </form>
            <div className="admin-panel-list admin-jobs-list">
              {jobs.length === 0 && <div>No jobs listed.</div>}
              {jobs.map((job, idx) => (
                <div key={idx} className="admin-panel-list-item admin-job-item">
                  {editJobIdx === idx ? (
                    <form onSubmit={e => handleEditJob(e, idx, job.id)} className="admin-panel-edit-form admin-job-edit-form">
                      <div className="admin-input-group">
                        <input name="path" type="text" placeholder="Path" value={editJobForm.path} onChange={e => setEditJobForm(f => ({ ...f, path: e.target.value }))} className="admin-input" />
                      </div>
                      <div className="admin-input-group">
                        <input name="agency" type="text" placeholder="Agency/Department" value={editJobForm.agency} onChange={e => setEditJobForm(f => ({ ...f, agency: e.target.value }))} className="admin-input" />
                      </div>
                      <div className="admin-input-group">
                        <input name="title" type="text" placeholder="Job Title" value={editJobForm.title} onChange={e => setEditJobForm(f => ({ ...f, title: e.target.value }))} className="admin-input" />
                      </div>
                      <div className="admin-input-group">
                        <input name="location" type="text" placeholder="Location" value={editJobForm.location} onChange={e => setEditJobForm(f => ({ ...f, location: e.target.value }))} className="admin-input" />
                      </div>
                      <div className="admin-job-actions">
                        <button type="submit" className="admin-btn admin-btn-neutral admin-job-edit-btn">Edit</button>
                        <button type="button" onClick={() => setEditJobIdx(null)} className="admin-btn admin-btn-danger admin-job-delete-btn">Delete</button>
                      </div>
                      {jobError && <div className="admin-error">{jobError}</div>}
                    </form>
                  ) : (
                    <div className="admin-job-details">
                      <div className="admin-job-path"><b>{job.path}</b></div>
                      <div className="admin-job-agency"><b>{job.agency}</b></div>
                      <div className="admin-job-title">{job.title}</div>
                      <div className="admin-job-location">{job.location}</div>
                      <div className="admin-job-actions">
                        <button onClick={() => { setEditJobIdx(idx); setEditJobForm(job); }} className="admin-btn admin-btn-neutral admin-job-edit-btn">Edit</button>
                        <button onClick={() => handleDeleteJob(job.id)} className="admin-btn admin-btn-danger admin-job-delete-btn">Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      }
    }
    return null;
  };

  // Fetch jobs
  useEffect(() => {
    if (isAuthenticated) {
      fetch(`${API_URL}/api/jobs`)
        .then(async res => {
          const headers = await getAuthHeaders();
          const data = await res.json();
          setJobs(data);
        });
    }
  }, [isAuthenticated]);

  // Add job
  const handleAddJob = async (e) => {
    e.preventDefault();
    setJobError('');
    if (!jobForm.path || !jobForm.agency || !jobForm.title || !jobForm.location) {
      setJobError('All fields are required.');
      return;
    }
    console.log('Submitting jobForm:', jobForm);
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/api/jobs`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(jobForm)
    });
    if (res.ok) {
      const newJob = await res.json();
      setJobs(jobs => [newJob, ...jobs]);
      setJobForm({ path: '', agency: '', title: '', location: '' });
    } else {
      setJobError('Failed to add job.');
    }
  };

  // Edit job
  const handleEditJob = async (e, idx, id) => {
    e.preventDefault();
    setJobError('');
    if (!editJobForm.path || !editJobForm.agency || !editJobForm.title || !editJobForm.location) {
      setJobError('All fields are required.');
      return;
    }
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/api/jobs/${id}`, {
      method: 'PUT',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(editJobForm)
    });
    if (res.ok) {
      const updatedJob = await res.json();
      setJobs(jobs => jobs.map((j, jdx) => jdx === idx ? updatedJob : j));
      setEditJobIdx(null);
    } else {
      setJobError('Failed to update job.');
    }
  };

  // Delete job
  const handleDeleteJob = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/api/jobs/${id}`, { method: 'DELETE', headers });
    if (res.ok) {
      setJobs(jobs => jobs.filter(j => j.id !== id));
    } else {
      alert('Failed to delete job.');
    }
  };

  const handleDeleteArticle = async (id) => {
    setArticleToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteArticle = async () => {
    if (!articleToDelete) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/api/news/${articleToDelete}`, { method: 'DELETE', headers });
      if (res.ok) {
        fetchArticles();
      } else {
        alert('Failed to delete article.');
      }
    } catch (err) {
      alert('Network error');
    }
    setLoading(false);
    setShowDeleteModal(false);
    setArticleToDelete(null);
  };

  if (isAuthenticated) {
    return (
      <div className="admin-container">
        {showDeleteModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal">
              <button onClick={() => { setShowDeleteModal(false); setArticleToDelete(null); }} className="admin-modal-close" aria-label="Close">×</button>
              <h3 className="admin-modal-title">Delete Article</h3>
              <p className="admin-modal-text">Are you sure you want to delete this article? This action cannot be undone.</p>
              <div className="admin-modal-actions">
                <button onClick={() => { setShowDeleteModal(false); setArticleToDelete(null); }} className="admin-btn admin-btn-secondary">Cancel</button>
                <button onClick={confirmDeleteArticle} className="admin-btn admin-btn-danger" disabled={loading}>
                  {loading ? 'Deleting...' : 'Delete Article'}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="admin-header-row">
          <h1 className="admin-title">Admin Dashboard</h1>
          <button
            onClick={() => instance.logoutPopup()}
            className="admin-btn admin-btn-logout"
          >
            Logout
          </button>
        </div>
        <div className="admin-header-spacer" />
        <div className="admin-main-content">
          {expandedPanel ? (
            <>
              <div className="admin-panel-expanded-row">
                <div className="admin-panel-expanded">
                  <button onClick={() => setExpandedPanel(null)} className="admin-panel-close" aria-label="Close">×</button>
                  {renderPanel(expandedPanel, false)}
                </div>
              </div>
              <div className="admin-panel-minimal-row">
                {PANEL_KEYS.filter(key => key !== expandedPanel).map(key => (
                  <div key={key} className="admin-panel-minimal" onClick={() => { setExpandedPanel(key); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  {renderPanel(key, true)}
                </div>
              ))}
              </div>
            </>
          ) : (
            <div className="admin-panel-grid">
              {PANEL_KEYS.map(key => (
                <div key={key} className="admin-panel-minimal" onClick={() => setExpandedPanel(key)}>
                  {renderPanel(key, true)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Replace login form with MSAL login
  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <h2>Admin Login</h2>
        <button onClick={() => instance.loginPopup(loginRequest)} className="admin-btn admin-btn-primary">
          Sign in with Microsoft
        </button>
      </div>
    );
  }
} 